import { useRef, useEffect } from 'react';

/**
 * A custom `useEventListener` hooks
 * https://github.com/donavon/use-event-listener
 *
 * @param {String} eventName The event name
 * @param {Function} handler A function that will be called whenever eventName fires on element.
 * @param {Node} element An optional element to listen on. Defaults to global (i.e., window).
 */

const useEventListener = (
  eventName: string,
  handler: (event?: string) => void,
  element = global
) => {
  const savedHandler = useRef<(event?: string) => void>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) {
      return;
    }

    const eventListener = (event: string) => savedHandler.current?.(event);
    // @ts-ignore window.addEventListener
    element.addEventListener(eventName, eventListener);
    return () => {
      // @ts-ignore window.removeEventListener
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
};

export default useEventListener;
